/**
 * The calendar date a KYC form carries, between the wire and a date picker.
 *
 * - The wire value is a **zoneless day**: `YYYY-MM-DD`, the day the user
 *   tapped, identical from every timezone. The server reads it as a UTC day
 *   (`new Date(value + 'T00:00:00Z')`), and so does `validateForm`.
 * - `Date` objects here are local midnight of that day, never a conversion of
 *   it: `new Date('1990-12-10')` is UTC midnight, which is the 9th behind UTC,
 *   so the picker would open and save a day early.
 * - "Today" is therefore the **UTC** day — the clock the not-future rule is
 *   judged on — so a user ahead of UTC cannot pick a day the server refuses.
 */

/** How many years back the picker will go. Older than any living applicant. */
const OLDEST_YEARS = 120

/** The wire format, read off a `Date` the picker handed back. */
export function toCalendarDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * The date a stored answer names, or `undefined` when it names none.
 *
 * The round trip is what rejects `2026-02-30`: `Date` rolls an impossible day
 * forward into the next month rather than refusing it.
 */
export function parseCalendarDate(value: string): Date | undefined {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (parts === null) {
    return undefined
  }
  const [, year, month, day] = parts
  const parsed = new Date(Number(year), Number(month) - 1, Number(day))
  return toCalendarDate(parsed) === value ? parsed : undefined
}

/**
 * The answer as the user's own locale writes it, or the answer itself when
 * that cannot be formatted — an unformatted date still reads as the date.
 */
export function formatCalendarDate(value: string, locale: string): string {
  const date = parseCalendarDate(value)
  if (date === undefined) {
    return value
  }
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date)
  } catch {
    // Not every runtime ships a full ICU, and a date is not worth a throw.
    return value
  }
}

/**
 * The bounds the picker offers.
 *
 * - A birth date in the future is never right, and the provider answers one
 *   with an empty 400 the user reads as an SDK failure.
 * - The ceiling is the **UTC** day, because that is the clock the server's
 *   not-future rule is judged on. Anyone ahead of UTC would otherwise be
 *   offered a day their own submission comes back refused for.
 */
export function birthDateRange(now = new Date()): {
  minimumDate: Date
  maximumDate: Date
} {
  const year = now.getUTCFullYear()
  const month = now.getUTCMonth()
  const day = now.getUTCDate()
  return {
    // Local midnight of the UTC day: the picker highlights the day itself,
    // and `toCalendarDate` writes exactly the day highlighted.
    minimumDate: new Date(year - OLDEST_YEARS, month, day),
    maximumDate: new Date(year, month, day),
  }
}
